import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { FoldersService } from '../services/folders.service';
import { FolderBrowserComponent } from '../folder-browser/folder-browser.component';

/**
 * Embedded documents tab for the custom-object detail view.
 *
 * Resolves `/objects/<objectType>/<objectId>` via `getFolderByPath`, which lazy-creates the
 * managed leaf on first access (see `FolderService.findByPath` on the backend). The resolved
 * folder id becomes the browser's `rootFolderId`, so navigation stays scoped to that subtree.
 *
 * `ViewEncapsulation.None` so the spinner override SCSS reaches the PrimeNG-rendered
 * `.p-progressspinner` element (which doesn't carry the host's `_ngcontent-` attribute).
 * Selectors in the SCSS are uniquely prefixed with `object-documents-tab__*` to keep global
 * leakage contained.
 */
@Component({
  selector: 'tf-object-documents-tab',
  imports: [TranslateModule, ProgressSpinnerModule, FolderBrowserComponent],
  templateUrl: './object-documents-tab.component.html',
  styleUrl: './object-documents-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ObjectDocumentsTabComponent {
  private readonly foldersService = inject(FoldersService);

  readonly objectType = input.required<string>();
  readonly objectId = input.required<string>();

  /**
   * Reference to the embedded `<tf-folder-browser>` instance so the host page (e.g.
   * `RecordPageComponent`'s AI page-context factory) can read the user's current selection,
   * breadcrumbs, and active smart view without coupling to internal details. `undefined` until
   * the inner browser actually mounts (the `@if` chain in the template gates it on
   * `rootFolderId` resolving).
   */
  readonly folderBrowser = viewChild(FolderBrowserComponent);

  protected readonly rootFolderId = signal<string | null>(null);
  protected readonly isResolving = signal(true);
  protected readonly hasError = signal(false);

  protected readonly path = computed(() => `/objects/${this.objectType()}/${this.objectId()}`);

  constructor() {
    effect(() => {
      const path = this.path();
      this.resolve(path);
    });
  }

  protected reload(): void {
    this.resolve(this.path());
  }

  private async resolve(path: string): Promise<void> {
    this.isResolving.set(true);
    this.hasError.set(false);
    try {
      const folder = await this.foldersService.getByPath(path);
      this.rootFolderId.set(folder.id ?? null);
    } catch {
      this.rootFolderId.set(null);
      this.hasError.set(true);
    } finally {
      this.isResolving.set(false);
    }
  }
}
